<template>
  <div>
    <div v-if="loading" class="text-center pa-12">
      <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
    </div>

    <div v-else-if="orders.length === 0" class="text-center pa-12 grey--text">
      <v-icon size="64" color="grey darken-1">mdi-package-variant-closed-remove</v-icon>
      <div class="mt-3 subtitle-1">Du hast noch keine Bestellungen</div>
      <div class="caption">Bestellungen erscheinen hier, sobald du den ersten Auftrag abschickst</div>
    </div>

    <div v-else>
      <div v-for="order in orders" :key="order._id" class="order-card" @click="$emit('open-detail', order)">
        <!-- Header -->
        <div class="order-header">
          <div>
            <div class="order-id">
              <v-icon small color="grey">mdi-pound</v-icon>
              {{ order._id.slice(-6).toUpperCase() }}
            </div>
            <div class="order-date">{{ formatDate(order.createdAt) }}</div>
          </div>
          <v-chip small :color="statusColor(order.status)" dark>
            <v-icon left x-small>{{ statusIcon(order.status) }}</v-icon>
            {{ statusLabel(order.status) }}
          </v-chip>
        </div>

        <!-- Status-Tracker -->
        <div class="status-tracker">
          <div
            v-for="(step, idx) in statusSteps"
            :key="step.value"
            class="step"
            :class="{ active: isStepActive(order.status, step.value), current: order.status === step.value }">
            <div class="step-icon">
              <v-icon size="18" :color="isStepActive(order.status, step.value) ? 'white' : 'grey darken-1'">{{ step.icon }}</v-icon>
            </div>
            <div class="step-label">{{ step.label }}</div>
            <div
              v-if="idx < statusSteps.length - 1"
              class="step-line"
              :class="{ active: isStepActive(order.status, statusSteps[idx + 1].value) }"></div>
          </div>
        </div>

        <!-- Items-Preview -->
        <div class="order-items-preview">
          <span v-for="(item, idx) in order.items.slice(0, 3)" :key="idx" class="item-chip"> {{ item.quantity }}× {{ item.productTitle }} </span>
          <span v-if="order.items.length > 3" class="caption grey--text ml-2"> +{{ order.items.length - 3 }} weitere </span>
        </div>

        <!-- Footer -->
        <div class="order-footer">
          <div class="caption grey--text">
            <v-icon small color="grey">mdi-store</v-icon>
            {{ order.studio ? order.studio.name : "-" }}
          </div>
          <div v-if="order.comments && order.comments.length > 0" class="caption grey--text">
            <v-icon small color="grey">mdi-comment-multiple</v-icon>
            {{ order.comments.length }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MyOrdersList",
  props: {
    orders: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      statusSteps: [
        { value: "received", label: "Erhalten", icon: "mdi-inbox-arrow-down" },
        { value: "confirmed", label: "Bestätigt", icon: "mdi-check-circle" },
        { value: "shipping", label: "Unterwegs", icon: "mdi-truck-fast" },
        { value: "delivered", label: "Geliefert", icon: "mdi-package-variant" },
      ],
    }
  },
  methods: {
    formatDate(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    statusColor(s) {
      if (s === "delivered") return "green"
      if (s === "shipping") return "blue"
      if (s === "confirmed") return "amber darken-2"
      return "grey darken-1"
    },
    statusIcon(s) {
      const step = this.statusSteps.find((x) => x.value === s)
      return step ? step.icon : "mdi-circle"
    },
    statusLabel(s) {
      const step = this.statusSteps.find((x) => x.value === s)
      return step ? step.label : "Unbekannt"
    },
    isStepActive(currentStatus, stepValue) {
      const order = ["received", "confirmed", "shipping", "delivered"]
      return order.indexOf(currentStatus) >= order.indexOf(stepValue)
    },
  },
}
</script>

<style scoped>
.order-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.order-card:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(0, 150, 64, 0.4);
  transform: translateY(-2px);
}

.order-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.order-id {
  font-weight: 700;
  font-size: 15px;
  color: #fff;
}

.order-date {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
}

/* Status Tracker */
.status-tracker {
  display: flex;
  align-items: flex-start;
  margin: 16px 0 20px;
  padding: 0 4px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
}

.step-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.1);
  z-index: 2;
  transition: all 0.3s ease;
}

.step.active .step-icon {
  background: #009640;
  border-color: #009640;
  box-shadow: 0 0 0 4px rgba(0, 150, 64, 0.2);
}

.step.current .step-icon {
  background: #009640;
  border-color: #4ade80;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.3), 0 0 12px rgba(0, 150, 64, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.3), 0 0 12px rgba(0, 150, 64, 0.6);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(74, 222, 128, 0.15), 0 0 20px rgba(0, 150, 64, 0.8);
  }
}

.step-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 6px;
  text-align: center;
  font-weight: 500;
}

.step.active .step-label {
  color: #fff;
}

.step-line {
  position: absolute;
  top: 18px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1;
}

.step-line.active {
  background: #009640;
}

@media (max-width: 600px) {
  .step-label {
    font-size: 9.5px;
  }
}

.order-items-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
  align-items: center;
}

.item-chip {
  background: rgba(255, 255, 255, 0.07);
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.order-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.order-footer .v-icon {
  margin-right: 4px;
}
</style>
